import "./ModalAdjustInformation.scss";
import {
  Avatar,
  Checkbox,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Typography,
} from "antd";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FieldSelect } from "shared/components";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import { getLocaleDatePickerByI18n } from "shared/helpers/getLocaleDatePickerByI18n";
import TextArea from "antd/lib/input/TextArea";
import { debounce } from "lodash";
import {
  fetchInstagramList,
  fetchSpotifyArtists,
  fetchYoutubeArtists,
  requestArtistInformationAdjustment,
} from "shared/service/StrmService";
import snakeToCamelCase from "shared/helpers/snakeToCamelCase";
import { RequestArtistInformationAdjustment } from "shared/interfaces/RequestArtistInformationAdjustment";

interface ModalAdjustInformationProps {
  onClose: () => void;
  show: boolean;
  artist: any;
}

const ModalAdjustInformation: React.FC<ModalAdjustInformationProps> = ({
  onClose,
  show,
  artist,
}) => {
  const { t, i18n } = useTranslation("radar");
  const [form] = Form.useForm();
  const { genreOptions } = useSelector((state: any) => state.genres);
  const { countries } = useSelector((state: any) => state.countries);
  const { loggedUser } = useSelector((state: any) => state.users);

  const [loading, setLoading] = useState(false);
  const [spotifyArtists, setSpotifyArtists] = useState([]);
  const [youtubeArtists, setYoutubeArtists] = useState([]);
  const [instagramArtists, setInstagramArtists] = useState([]);
  const [fetching, setFetching] = useState({
    spotify: false,
    youtube: false,
    instagram: false,
  });

  const fetchArtists = useCallback(
    async (
      term: string,
      fetchFunction: Function,
      setState: Function,
      type: "spotify" | "youtube" | "instagram"
    ) => {
      if (!term) return;
      setFetching((prev) => ({ ...prev, [type]: true }));
      try {
        const response = await fetchFunction(term);

        if (response.length === 0) {
          setState([
            {
              id: "",
              name: t(
                "pages.ArtistProfile.ModalAdjustInformation.profileNotFound"
              ),
              image: "",
            },
          ]);
        } else {
          const artists = snakeToCamelCase(response).map((artist: any) => ({
            id:
              artist.snippet?.channelId || artist.id || artist.searchByUsername,
            name:
              artist.name ||
              artist.snippet?.title ||
              artist.profile?.artistName,
            image:
              artist.snippet?.thumbnails?.medium?.url ||
              artist.profile?.pictureUrl ||
              artist?.images[0]?.url,
          }));
          setState(artists);
        }
      } catch (error) {
        console.error(`Error fetching ${type} artists:`, error);
        setState([]);
      } finally {
        setFetching((prev) => ({ ...prev, [type]: false }));
      }
    },
    [t]
  );

  const onFinish = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      if (
        !values.enableGenre &&
        !values.enableCountry &&
        !values.enableDateFirstRelease &&
        !values.enableSpotify &&
        !values.enableYoutube &&
        !values.enableInstagram &&
        !values.enableOthers
      ) {
        message.error(
          t("pages.ArtistProfile.ModalAdjustInformation.selectSomeOptions")
        );
        return;
      }

      const request: RequestArtistInformationAdjustment = {
        additionalInformation: values.additionalInformation,
        senderName: loggedUser.name,
        senderEmail: loggedUser.login,
        artistIdStrm: artist?.artistIdStrm,
        artistName: artist?.artistName,
        genres: values.enableGenre ? values.genres : null,
        country: values.enableCountry ? values.country : null,
        firstRelease: values.enableDateFirstRelease
          ? values.dateFirstRelease.format("YYYY-MM-DD")
          : null,
        spotifyId: values.enableSpotify ? values.spotifyId : null,
        youtubeId: values.enableYoutube ? values.youtubeId : null,
        instagram: values.enableInstagram ? values.instagramId : null,
      };

      await requestArtistInformationAdjustment(request);
      message.success(
        t("pages.ArtistProfile.ModalAdjustInformation.success"),
        10
      );
      onClose();
      form.resetFields();
    } catch (error) {
      message.error(
        t("pages.ArtistProfile.ModalAdjustInformation.fatalError"),
        10
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={show}
      className="modal-adjust-information"
      footer={false}
      onCancel={onClose}
    >
      <Typography.Title className="title">
        {t("pages.ArtistProfile.ModalAdjustInformation.title")}
      </Typography.Title>
      <Form
        layout="vertical"
        form={form}
        initialValues={{ name: loggedUser.name, email: loggedUser.login }}
        onFinish={onFinish}
      >
        <Form.Item
          name="name"
          label={t("pages.ArtistProfile.ModalAdjustInformation.name")}
        >
          <Input readOnly />
        </Form.Item>
        <Form.Item
          name="email"
          label={t("pages.ArtistProfile.ModalAdjustInformation.email")}
        >
          <Input readOnly />
        </Form.Item>
        <p className="title message-information-to-adjust">
          {t("pages.ArtistProfile.ModalAdjustInformation.informationToAdjust")}
        </p>
        <Form.Item name="enableGenre" valuePropName="checked">
          <Checkbox>
            {t("pages.ArtistProfile.ModalAdjustInformation.genre")}
          </Checkbox>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.enableGenre !== currentValues.enableGenre
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("enableGenre") && (
              <Form.Item
                name="genres"
                rules={[
                  {
                    required: true,
                    message: t(
                      "pages.ArtistProfile.ModalAdjustInformation.selectGenre"
                    ),
                  },
                ]}
              >
                <FieldSelect
                  mode="multiple"
                  showSelectAll={false}
                  dataList={genreOptions}
                  maxSelectedCount={3}
                  placeholder={t(
                    "pages.ArtistProfile.ModalAdjustInformation.selectGenre"
                  )}
                />
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item name="enableCountry" valuePropName="checked">
          <Checkbox>
            {t("pages.ArtistProfile.ModalAdjustInformation.country")}
          </Checkbox>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.enableCountry !== currentValues.enableCountry
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("enableCountry") && (
              <Form.Item
                name="country"
                rules={[
                  {
                    required: true,
                    message: t(
                      "pages.ArtistProfile.ModalAdjustInformation.selectCountry"
                    ),
                  },
                ]}
              >
                <FieldSelect
                  showSelectAll={false}
                  dataList={countries.map((option: any) => ({
                    label: getI18nCoutry(option.code, i18n, countries),
                    value: option.code,
                  }))}
                  placeholder={t(
                    "pages.ArtistProfile.ModalAdjustInformation.selectCountry"
                  )}
                  showSearch
                />
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item name="enableDateFirstRelease" valuePropName="checked">
          <Checkbox>
            {t("pages.ArtistProfile.ModalAdjustInformation.dateFirstRelease")}
          </Checkbox>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.enableDateFirstRelease !==
            currentValues.enableDateFirstRelease
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("enableDateFirstRelease") && (
              <Form.Item
                name="dateFirstRelease"
                rules={[
                  {
                    required: true,
                    message: t(
                      "pages.ArtistProfile.ModalAdjustInformation.dateFirstRelease"
                    ),
                  },
                ]}
              >
                <DatePicker
                  locale={getLocaleDatePickerByI18n("pt")}
                  placeholder="DD/MM/YYYY"
                  format="DD/MM/YYYY"
                  className="datepicker-first-release"
                />
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item name="enableSpotify" valuePropName="checked">
          <Checkbox>
            {t("pages.ArtistProfile.ModalAdjustInformation.spotify")}
          </Checkbox>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.enableSpotify !== currentValues.enableSpotify
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("enableSpotify") && (
              <Form.Item
                name="spotifyId"
                label={t(
                  "pages.ArtistProfile.ModalAdjustInformation.inputSpotify"
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      "pages.ArtistProfile.ModalAdjustInformation.invalidUrl"
                    ),
                  },
                ]}
              >
                <Select
                  showSearch
                  onSearch={debounce(
                    (term) =>
                      fetchArtists(
                        term,
                        fetchSpotifyArtists,
                        setSpotifyArtists,
                        "spotify"
                      ),
                    500
                  )}
                  loading={fetching.spotify}
                  filterOption={false}
                >
                  {spotifyArtists.map((artist: any) => (
                    <Select.Option key={artist.id} value={artist.id}>
                      {artist.image && (
                        <Avatar style={{ marginRight: 6 }} src={artist.image} />
                      )}
                      {artist.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item name="enableYoutube" valuePropName="checked">
          <Checkbox>
            {t("pages.ArtistProfile.ModalAdjustInformation.youtube")}
          </Checkbox>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.enableYoutube !== currentValues.enableYoutube
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("enableYoutube") && (
              <Form.Item
                name="youtubeId"
                label={t(
                  "pages.ArtistProfile.ModalAdjustInformation.inputYoutube"
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      "pages.ArtistProfile.ModalAdjustInformation.invalidUrl"
                    ),
                  },
                ]}
              >
                <Select
                  showSearch
                  onSearch={debounce(
                    (term) =>
                      fetchArtists(
                        term,
                        fetchYoutubeArtists,
                        setYoutubeArtists,
                        "youtube"
                      ),
                    500
                  )}
                  loading={fetching.youtube}
                  filterOption={false}
                >
                  {youtubeArtists.map((artist: any) => (
                    <Select.Option key={artist.id} value={artist.id}>
                      {artist.image && (
                        <Avatar style={{ marginRight: 6 }} src={artist.image} />
                      )}
                      {artist.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item name="enableInstagram" valuePropName="checked">
          <Checkbox>
            {t("pages.ArtistProfile.ModalAdjustInformation.instagram")}
          </Checkbox>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.enableInstagram !== currentValues.enableInstagram
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("enableInstagram") && (
              <Form.Item
                name="instagramId"
                label={t(
                  "pages.ArtistProfile.ModalAdjustInformation.inputInstagram"
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      "pages.ArtistProfile.ModalAdjustInformation.invalidUrl"
                    ),
                  },
                ]}
              >
                <Select
                  showSearch
                  onSearch={debounce(
                    (term) =>
                      fetchArtists(
                        term,
                        fetchInstagramList,
                        setInstagramArtists,
                        "instagram"
                      ),
                    500
                  )}
                  loading={fetching.instagram}
                  filterOption={false}
                >
                  {instagramArtists.map((artist: any) => (
                    <Select.Option key={artist.id} value={artist.id}>
                      {artist.image && (
                        <Avatar
                          style={{ marginRight: "6px" }}
                          src={artist.image}
                        />
                      )}
                      {artist.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item name="enableOthers" valuePropName="checked">
          <Checkbox>
            {t("pages.ArtistProfile.ModalAdjustInformation.others")}
          </Checkbox>
        </Form.Item>
        <Form.Item
          name="additionalInformation"
          label={t(
            "pages.ArtistProfile.ModalAdjustInformation.enterMoreInformation"
          )}
          style={{ marginTop: "25px" }}
          rules={[
            {
              required: true,
              message: t(
                "pages.ArtistProfile.ModalAdjustInformation.enterMoreInformation"
              ),
            },
          ]}
        >
          <TextArea rows={4} />
        </Form.Item>
        <Row justify="end">
          <button className="botao-branco" type="submit" disabled={loading}>
            {t("pages.ArtistProfile.ModalAdjustInformation.confirm")}
          </button>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalAdjustInformation;
